import { FC, useContext, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import NotificationDispatch, { showErrorNotification } from "../../../../context/notificationContext";
import { useSearchContext } from "../../../../context/searchContext";
import { useDebounce } from "../../../../hooks/useDebounce";
import { useEntityAccesses } from "../../../../hooks/useEntityAccesses";
import { useFilterSearch } from "../../../../hooks/useFilterSearch";
import useListFilters from "../../../../hooks/useListFilters";
import useI18n from "../../../../hooks/useTranslations";
import { Handle } from "../../../../http/vertigrip/types/handles";
import { useHandlesApi } from "../../../../http/vertigrip/useHandles";
import { PaginationQuery } from "../../../../types";
import FlexContent from "../../../ui/FlexContent";
import { getDeleteAction, getEditAction } from "../../../ui/ListItemMenu";
import ModalDialog from "../../../ui/ModalDialog";
import SearchBar from "../../../ui/SearchBar";
import Table from "../../../ui/Table";
import Visibility from "../../../ui/Visibility";
import { VisibilityScopeValue } from "../../../ui/VisibilityScope";
import CreateHandleModalDialogButton from "./CreateHandleModalDialogButton";
import EditHandleModalDialog from "./EditHandleModalDialog";

type Props = {
  active?: boolean;
};

const Handles: FC<Props> = ({ active }) => {
  const i18n = useI18n();
  const dispatch = useContext(NotificationDispatch);
  const filters = useListFilters({ key: "handles" });
  const aborter = useRef(new AbortController());
  const { setActions, setTitle } = useSearchContext();
  const { searchTerm, handleSearchChange } = useFilterSearch(filters, aborter, "handles");

  const isActiveTab = active ?? true;

  const [itemToEdit, setItemToEdit] = useState<Handle>();
  const [itemToDelete, setItemToDelete] = useState<Handle>();
  const { accessesAction, accessesDialog } = useEntityAccesses<Handle>("vertigrip.handle");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handelsApi = useHandlesApi();

  const updateVisibility = async (id: string, visibility: VisibilityScopeValue) => {
    try {
      await handelsApi.updateAdminHandleVisibility(id, { visibility });
    } catch (err) {
      dispatch(showErrorNotification(i18n, err));
    }
  };

  const debouncedValue = useDebounce(searchTerm);

  const query: PaginationQuery = {
    ...filters.state,
    q: debouncedValue,
  };

  const { error, data } = handelsApi.useAdminHandles(query, aborter.current.signal);

  useEffect(() => {
    if (!isActiveTab) return;

    setTitle(i18n.translation.handles.plural);
    setActions(
      <>
        <SearchBar placeholder={i18n.translation.handles.search} value={searchTerm} onChange={handleSearchChange} />
        <CreateHandleModalDialogButton />
      </>,
    );

    return () => {
      setTitle(undefined);
      setActions(null);
    };
  }, [isActiveTab, searchTerm]);

  return (
    <FlexContent padding>
      <Table<Handle>
        isTall
        filters={filters}
        head={
          <tr>
            <th align="left">{i18n.translation.common.code}</th>
            <th align="left">{i18n.translation.common.description}</th>
            <th>{i18n.translation.common.visibility}</th>
            <th></th>
          </tr>
        }
        items={data?.items}
        renderRow={(item) => (
          <>
            <td>
              <Link to={`/admin/items/${item.id}`}>{item.code}</Link>
            </td>
            <td>{item.description}</td>
            <td align="center">
              <Visibility value={item.visibility} onChange={(value) => updateVisibility(item.id, value)} />
            </td>
          </>
        )}
        onRowClick={(item) => setItemToEdit(item)}
        rowActions={(item) => [
          getEditAction(i18n, () => setItemToEdit(item)),
          accessesAction(item),
          getDeleteAction(i18n, () => setItemToDelete(item), true),
        ]}
        total={data?.total}
        error={!!error}
      />
      {accessesDialog}
      {!!itemToDelete && (
        <ModalDialog
          onClose={() => setItemToDelete(undefined)}
          title={i18n.translation.handles.delete}
          isSubmitting={isSubmitting}
          actions={[
            {
              title: i18n.translation.common.cancel,
              onClick: () => setItemToDelete(undefined),
              type: "secondary",
            },
            {
              title: i18n.translation.common.yes,
              onClick: () => {
                setIsSubmitting(true);
                handelsApi
                  .deleteAdminHandle(itemToDelete.id)
                  .then(() => setItemToDelete(undefined))
                  .catch((err) => dispatch(showErrorNotification(i18n, err)))
                  .finally(() => setIsSubmitting(false));
              },
            },
          ]}
        >
          {i18n.translation.handles.questions.delete}
        </ModalDialog>
      )}
      <EditHandleModalDialog onClose={() => setItemToEdit(undefined)} handle={itemToEdit} />
    </FlexContent>
  );
};

export default Handles;
